import React, { useEffect, useState } from 'react';
import { X, Clock } from 'lucide-react';
import toast from 'react-hot-toast';
import studyBuilderService from '../../services/studyBuilderService';

/**
 * FECOA-1238: Version history for a live schedule — the original protocol plus
 * every mid-study amendment, newest first. Read-only audit surface.
 *
 * @param {boolean} open
 * @param {() => void} onClose
 * @param {string} scheduleId - the live Schedule UUID
 */
const ScheduleVersionHistoryPanel = ({ open, onClose, scheduleId }) => {
  const [versions, setVersions] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open || !scheduleId) return;
    let cancelled = false;
    setLoading(true);
    studyBuilderService
      .getScheduleVersions(scheduleId)
      .then((data) => {
        if (!cancelled) setVersions(Array.isArray(data) ? data : (data?.results || []));
      })
      .catch(() => {
        if (!cancelled) toast.error('Failed to load schedule version history.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, scheduleId]);

  if (!open) return null;

  const fmt = (iso) => {
    if (!iso) return '—';
    try {
      return new Date(iso).toLocaleString();
    } catch {
      return iso;
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex justify-end bg-gray-950/40 dark:bg-black/70">
      <div className="flex h-full w-full max-w-md flex-col bg-white shadow-overlay ring-1 ring-gray-900/5 dark:bg-gray-800 dark:ring-white/10">
        <div className="flex items-start justify-between gap-4 border-b border-gray-100 px-6 py-4 dark:border-white/[0.06]">
          <h2 className="text-base font-semibold text-gray-900 dark:text-gray-50">
            Schedule version history
          </h2>
          <button
            onClick={onClose}
            className="-mr-2 flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-700 dark:hover:bg-white/[0.06] dark:hover:text-gray-200"
            aria-label="Close"
          >
            <X className="h-[18px] w-[18px]" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-5">
        {loading && (
          <p className="text-sm text-gray-500 dark:text-gray-400">Loading…</p>
        )}

        {!loading && versions.length === 0 && (
          <p className="text-sm text-gray-500 dark:text-gray-400">No version history available.</p>
        )}

        <ol className="space-y-3">
          {versions.map((v) => (
            <li
              key={v.id}
              className="rounded-lg border border-gray-200/70 bg-gray-50/80 p-4 dark:border-white/[0.07] dark:bg-white/[0.03]"
            >
              <div className="mb-1 flex items-center justify-between gap-3">
                <span className="text-[13.5px] font-semibold text-gray-900 dark:text-gray-50">
                  Version {v.version_number}
                  {v.version_number === 1 && (
                    <span className="ml-2 text-xs font-normal text-gray-500 dark:text-gray-400">
                      (original protocol)
                    </span>
                  )}
                </span>
                <span className="flex items-center gap-1 text-xs tabular-nums text-gray-500 dark:text-gray-400">
                  <Clock className="h-3.5 w-3.5" />
                  {fmt(v.effective_from)}
                </span>
              </div>
              {v.reason && (
                <p className="mb-1 text-[13px] text-gray-700 dark:text-gray-300">{v.reason}</p>
              )}
              <p className="text-xs text-gray-500 dark:text-gray-400">
                {v.created_by ? `By ${v.created_by}` : 'System'} · created {fmt(v.created_at)}
              </p>
            </li>
          ))}
        </ol>
        </div>
      </div>
    </div>
  );
};

export default ScheduleVersionHistoryPanel;
